import {
  legendTemplateFieldNames,
  legendTemplateRemoveCrosshairValues,
  legendTemplateToFancyLegendProps,
  parseLegendTemplate,
} from '../../../util/legend';
import {prettifyMetricName} from '../../../util/plotHelpers/prettifyMetricName';
import {type Line} from '../../../util/plotHelpers/types';
import * as Run from '../../../util/runs';

type OverrideGroupedLegendValuesOptions = {
  getLegendTemplateByRunsetId: (runsetId: string | undefined) => string;
};

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

/**
 * Picks the number a grouped line "is" for legend purposes: the single point
 * of a bar/scatter chart, or the last plotted point of a line. This is the
 * value the chart draws, so it already reflects the group aggregation (mean,
 * min, max), any run sampling, smoothing, and grouping depth.
 *
 * Area series (`convertLinesToArea`, `convertAreaToPercentArea`) replace `y`
 * with the cumulative stack top and keep the series' own value in
 * `legendData.y`, so that is preferred when present.
 */
function plottedValue(line: Line): number | undefined {
  for (let i = line.data.length - 1; i >= 0; i--) {
    const point = line.data[i];
    if (point == null) {
      continue;
    }
    const y = finiteNumber(point.legendData?.y) ?? finiteNumber(point.y);
    if (y != null) {
      return y;
    }
  }
  return undefined;
}

function groupIdFor(line: Line): string | undefined {
  if (
    line.run == null ||
    line.groupKeys == null ||
    line.groupKeys.length === 0
  ) {
    return undefined;
  }
  return Run.uniqueId(line.run, line.groupKeys);
}

/**
 * A grouped line carries the `run` of one arbitrary member (the first
 * fetched run on the client path, the backend's `groupedRun` node otherwise),
 * so a legend field that names a summary metric resolves to that member's
 * value rather than the group's. When the metric is one the panel plots, the
 * group's value is the line itself. This rewrites `title` and `fancyTitle` of
 * grouped lines so summary legend fields for plotted metrics show the plotted
 * group value. Legend fields that aren't plotted metrics are left as they are.
 */
export function overrideGroupedLegendValues<T extends Line>(
  lines: T[],
  {getLegendTemplateByRunsetId}: OverrideGroupedLegendValuesOptions
): T[] {
  // group id -> metric name -> plotted value, from primary lines only
  const plottedByGroup = new Map<string, Map<string, number>>();
  for (const line of lines) {
    if (line.aux || line.metricName == null) {
      continue;
    }
    const groupId = groupIdFor(line);
    if (groupId == null) {
      continue;
    }
    const value = plottedValue(line);
    if (value == null) {
      continue;
    }
    let byMetric = plottedByGroup.get(groupId);
    if (byMetric == null) {
      byMetric = new Map();
      plottedByGroup.set(groupId, byMetric);
    }
    if (!byMetric.has(line.metricName)) {
      byMetric.set(line.metricName, value);
    }
  }
  if (plottedByGroup.size === 0) {
    return lines;
  }

  return lines.map(line => {
    const groupId = groupIdFor(line);
    if (groupId == null || line.run == null || line.metricName == null) {
      return line;
    }
    const byMetric = plottedByGroup.get(groupId);
    if (byMetric == null) {
      return line;
    }
    const template = getLegendTemplateByRunsetId(line.run.runsetInfo?.id);

    // Only fields outside `[[ ]]` are shown in the legend and bar labels;
    // crosshair fields already come from the plotted data.
    const summaryOverrides: Record<string, number> = {};
    for (const fieldName of legendTemplateFieldNames(
      legendTemplateRemoveCrosshairValues(template)
    )) {
      const key = Run.keyFromString(fieldName);
      if (key == null || key.section !== 'summary') {
        continue;
      }
      const value = byMetric.get(key.name);
      if (value != null) {
        summaryOverrides[key.name] = value;
      }
    }
    if (Object.keys(summaryOverrides).length === 0) {
      return line;
    }

    const displayRun = {
      ...line.run,
      summary: {...line.run.summary, ...summaryOverrides},
    };
    const groupKeys = line.groupKeys ?? [];
    const prettyMetric = prettifyMetricName(line.metricName);
    // Custom run names never apply to groups (Run.getCustomRunName), so the
    // customRunName argument is omitted.
    return {
      ...line,
      title: parseLegendTemplate(
        template,
        true,
        displayRun,
        groupKeys,
        prettyMetric
      ),
      fancyTitle: legendTemplateToFancyLegendProps(
        template,
        displayRun,
        groupKeys,
        prettyMetric,
        line.fancyTitle?.rootUrl
      ),
    };
  });
}
